import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  ChartColumn,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronRight,
  Globe,
  KeyRound,
  Languages,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sun,
  UserCog,
  UserMinus,
  UserPlus,
  UserRound,
  MapPin,
  MapPinHouse,
  MapPinned,
  Menu as MenuIcon,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { Avatar } from "@/components/avatar";
import { Logo, Mark } from "@/components/brand";
import { IconButton } from "@/components/ui/button";
import { Menu, MenuItem, MenuSeparator, menuItemClass } from "@/components/ui/menu";
import { SearchInput } from "@/components/ui/search-input";
import type { TranslationKey } from "@/constants/i18n";
import { can } from "@/utils/permissions";
import type { Profile } from "@/meta/types";
import { LOCALES } from "@/constants/i18n";
import { useLocaleStore, useTranslation } from "@/store/locale";
import { useThemeStore } from "@/store/theme";
import { COMING_SOON, type ComingSoonSlug } from "@/pages/ComingSoonPage";
import { cn } from "@/utils/utils";

export { PageHeader } from "@/components/ui/page";

/**
 * The menu as designed. `to` is set only where a route exists, so the rest
 * render as coming-soon pages rather than dead links.
 */
type AppPath =
  | "/"
  | "/provinces"
  | "/cities"
  | "/barangays"
  | "/configuration"
  | "/employees"
  | "/yellow-page"
  | "/non-members"
  | "/member-analytics"
  | "/member-population"
  | "/members";

interface NavItem {
  label: TranslationKey;
  icon: typeof ChartColumn;
  to?: AppPath;
  slug?: ComingSoonSlug;
  /** Any one of these codenames opens the entry; none means everyone sees it. */
  perms?: readonly string[];
}

/**
 * Destinations, grouped by what someone came here to do rather than by which
 * table they live in. A flat list of nine is nine decisions every time; five
 * labelled groups is one decision and then a short one.
 *
 * `Configuration` sits in its own group at the foot, away from the work: it is
 * where you change the rules, not where you do the job.
 */
const NAV: readonly { label?: TranslationKey; items: readonly NavItem[] }[] = [
  {
    items: [{ label: "nav.dashboard", icon: ChartColumn, to: "/" }],
  },
  {
    label: "nav.group.membership",
    items: [
      { label: "nav.members", icon: UserPlus, to: "/members", perms: ["view__member"] },
      {
        label: "nav.memberAnalytics",
        icon: ChartNoAxesCombined,
        to: "/member-analytics",
        perms: ["view__member"],
      },
      {
        label: "nav.nonMembers",
        icon: UserMinus,
        to: "/non-members",
        perms: ["view__non_member"],
      },
    ],
  },
  {
    label: "nav.group.directory",
    items: [
      { label: "nav.employees", icon: UserCog, to: "/employees", perms: ["view__user"] },
      {
        label: "nav.yellowPage",
        icon: BookOpen,
        to: "/yellow-page",
        perms: ["view__yellow_page"],
      },
    ],
  },
  {
    label: "nav.group.geography",
    items: [
      { label: "nav.provinces", icon: Globe, to: "/provinces", perms: ["view__province"] },
      { label: "nav.cities", icon: MapPinHouse, to: "/cities", perms: ["view__city"] },
      { label: "nav.barangays", icon: MapPinned, to: "/barangays", perms: ["view__barangay"] },
    ],
  },
  {
    label: "nav.maps",
    items: [
      {
        label: "nav.memberPopulation",
        icon: MapPin,
        to: "/member-population",
        perms: ["view__member"],
      },
    ],
  },
  {
    label: "nav.group.system",
    items: [
      {
        label: "nav.configuration",
        icon: Settings,
        to: "/configuration",
        perms: ["view__club", "view__business_type", "view__promo_code", "view__reason"],
      },
    ],
  },
];

const pathOf = (item: NavItem) => item.to ?? `/${item.slug ?? ""}`;
const ALL_ITEMS = NAV.flatMap((group) => group.items);

export function AppShell({
  profile,
  onSignOut,
  children,
}: {
  profile: Profile;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const t = useTranslation();
  const { pathname } = useLocation();
  // Collapsed lasts the tab; a rail nobody asked for on every load is a nag.
  const [collapsed, setCollapsed] = useState(false);
  // Below md the sidebar is a drawer: opened from the top bar, closed by a tap
  // outside, Escape, or following any link in it.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setDrawerOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // The box in the sidebar filters the menu. A search field that searched
  // nothing was the one control here that did not do what it looked like.
  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    // What the person may see comes first; a door they cannot open is not
    // listed, rather than listed and refused.
    const visible = NAV.map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.perms || can(profile, ...item.perms)),
    })).filter((group) => group.items.length > 0);
    if (!needle) return visible;
    return visible
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => t(item.label).toLowerCase().includes(needle)),
      }))
      .filter((group) => group.items.length > 0);
  }, [filter, t, profile]);

  const current = useCurrentPageName(pathname);

  return (
    <div
      className={cn(
        // The shell is exactly the viewport and does not scroll; the content
        // column does. The sidebar is `h-dvh`, so a scrolling body slid the page
        // past the end of it and left a band of bare background under it.
        "grid h-dvh overflow-hidden",
        collapsed ? "md:grid-cols-[72px_1fr]" : "md:grid-cols-[223px_1fr]",
      )}
    >
      <a
        href="#main"
        className="bg-accent text-accent-fg text-callout sr-only rounded-lg px-4 py-2 font-medium focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50"
      >
        {t("shell.skipToContent")}
      </a>

      {drawerOpen ? (
        <button
          type="button"
          aria-label={t("shell.closeMenu")}
          onClick={() => setDrawerOpen(false)}
          className="bg-scrim fixed inset-0 z-30 md:hidden"
        />
      ) : null}

      <aside
        aria-label={t("shell.mainNav")}
        className={cn(
          "bg-sidebar border-sidebar-line fixed inset-y-0 left-0 z-40 flex w-[240px] max-w-[85vw] flex-col border-r",
          "transition-transform duration-[var(--duration-slow)] ease-[var(--ease-out-soft)]",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
          "md:sticky md:top-0 md:h-dvh md:w-auto md:max-w-none md:translate-x-0",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center gap-2 pr-3 pl-4",
            collapsed && "md:justify-center md:px-0",
          )}
        >
          <Link
            to="/"
            title={t("shell.system")}
            className="flex min-w-0 flex-1 items-center rounded-lg"
          >
            {collapsed ? (
              <>
                <Mark className="h-7 shrink-0 dark:hidden" />
                <Mark tone="light" className="hidden h-7 shrink-0 dark:block" />
              </>
            ) : (
              <>
                <Logo className="h-9 w-[100px] shrink-0 dark:hidden" />
                <Logo tone="light" className="hidden h-9 w-[100px] shrink-0 dark:block" />
              </>
            )}
            <span className="sr-only">{t("shell.system")}</span>
          </Link>
          <IconButton
            label={t("shell.closeMenu")}
            size="icon-sm"
            className="md:hidden"
            onClick={() => setDrawerOpen(false)}
          >
            <X aria-hidden />
          </IconButton>
          <IconButton
            label={collapsed ? t("shell.expand") : t("shell.collapse")}
            size="icon-sm"
            aria-pressed={collapsed}
            className={cn("hidden md:inline-flex", collapsed && "md:hidden")}
            onClick={() => setCollapsed(true)}
          >
            <PanelLeftClose aria-hidden />
          </IconButton>
        </div>

        {collapsed ? (
          <div className="hidden justify-center pb-2 md:flex">
            <IconButton
              label={t("shell.expand")}
              size="icon-sm"
              aria-pressed={collapsed}
              onClick={() => setCollapsed(false)}
            >
              <PanelLeftOpen aria-hidden />
            </IconButton>
          </div>
        ) : (
          <div className="px-3 pb-1">
            <SearchInput
              fieldSize="sm"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              onClear={() => setFilter("")}
              placeholder={t("shell.search")}
              aria-label={t("shell.navSearch")}
            />
          </div>
        )}

        <nav
          className="flex flex-1 flex-col gap-4 overflow-y-auto px-3 pt-3 pb-4"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("a")) setDrawerOpen(false);
          }}
        >
          {groups.map((group, index) => (
            <div key={group.label ?? `group-${index}`} className="flex flex-col gap-0.5">
              {group.label && !collapsed ? (
                <p className="text-eyebrow text-fg-subtle px-2.5 pt-1 pb-1.5">
                  {t(group.label)}
                </p>
              ) : null}
              {group.items.map((item) => (
                <NavLink
                  key={pathOf(item)}
                  item={item}
                  active={pathname === pathOf(item)}
                  collapsed={collapsed}
                  label={t(item.label)}
                />
              ))}
            </div>
          ))}
          {groups.length === 0 ? (
            <p className="text-fg-subtle text-subhead px-2.5 py-3">{t("shell.noNavMatch")}</p>
          ) : null}
        </nav>
      </aside>

      <div className="flex h-dvh min-w-0 flex-col overflow-hidden">
        {/* Above the scroller rather than sticky inside it: it is a fixed part
            of the frame now, not something the content slides under. */}
        <header className="bg-surface-overlay border-line z-20 flex min-w-0 shrink-0 items-center gap-3 border-b px-4 py-2.5 backdrop-blur-xl md:px-6">
          <IconButton
            label={t("shell.openMenu")}
            className="md:hidden"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon aria-hidden />
          </IconButton>
          <nav
            aria-label="Breadcrumb"
            className="text-fg-muted text-subhead flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden whitespace-nowrap"
          >
            <span className="hidden md:inline">{t("shell.system")}</span>
            <ChevronRight className="hidden size-3.5 shrink-0 md:inline" aria-hidden />
            <span className="text-fg truncate font-medium" aria-current="page">
              {current}
            </span>
          </nav>
          {/* The same two controls the signed-out screens float in the corner.
              Here there is a bar to put them in, so they go in it. */}
          <div className="flex shrink-0 items-center gap-2">
            <AccountMenu profile={profile} onSignOut={onSignOut} />
          </div>
        </header>
        {/* The one scroller. `min-h-0` so it may be shorter than its content
            rather than stretching the column past the viewport. */}
        {/* `isolate`: every z-index a page uses — sticky table headers, filter
            panels — stays inside the page, so nothing on it can paint over the
            header's menu, whatever number it picks. */}
        <main id="main" className="isolate min-h-0 min-w-0 flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * One row of the menu.
 *
 * The selected row is a filled accent pill: on a grey sidebar a tint alone is
 * too quiet to find at a glance, and where you are is the first question the
 * navigation has to answer.
 */
function NavLink({
  item,
  active,
  collapsed,
  label,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  label: string;
}) {
  const className = cn(
    "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-callout",
    "transition-[background-color,color] duration-[var(--duration-fast)]",
    collapsed && "md:justify-center md:px-0",
    // Tinted rather than filled, the way every other selected thing in the app
    // reads — the tabs, the account chips, the dashboard's icon squares. A
    // solid accent bar is louder than the page it is pointing at.
    active
      ? "bg-accent-soft text-accent font-medium [&_svg]:text-accent"
      : "text-sidebar-fg hover:bg-sidebar-hover [&_svg]:text-sidebar-icon",
  );
  const body = (
    <>
      <item.icon className="size-4.5 shrink-0" aria-hidden />
      {collapsed ? (
        <span className="sr-only">{label}</span>
      ) : (
        <span className="truncate">{label}</span>
      )}
    </>
  );
  const props = {
    title: label,
    className,
    "aria-current": active ? ("page" as const) : undefined,
  };
  return item.to ? (
    <Link to={item.to} {...props}>
      {body}
    </Link>
  ) : (
    <Link to={`/${item.slug ?? ""}`} {...props}>
      {body}
    </Link>
  );
}

/** The breadcrumb's last crumb: the name of whatever is on screen. */
function useCurrentPageName(pathname: string) {
  const t = useTranslation();
  const slug = pathname.slice(1);
  const navLabel = ALL_ITEMS.find((item) => pathOf(item) === pathname)?.label;
  if (navLabel) return t(navLabel);
  if (slug in COMING_SOON) return COMING_SOON[slug as ComingSoonSlug].title;
  if (pathname === "/employee-activity-log") return t("page.activityLog");
  if (pathname === "/member-activity-log") return t("page.memberActivityLog");
  if (pathname.startsWith("/members/")) return t("page.memberDetail");
  if (pathname === "/yellow-page-categories") return t("page.yellowPageCategories");
  if (pathname === "/profile") return t("page.profile");
  if (pathname === "/profile/password") return t("page.changePassword");
  return t("nav.dashboard");
}

/**
 * The person's own corner, top right: who is signed in, and only what is theirs
 * to change — profile, password, language, theme, and the way out.
 */
function AccountMenu({ profile, onSignOut }: { profile: Profile; onSignOut: () => void }) {
  const t = useTranslation();
  const name = profile.full_name ?? profile.user_name;

  // Two choices each, so a row shows the current one and a click takes the other.
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useLocaleStore((state) => state.setLocale);
  const nextLocale = LOCALES.find((entry) => entry.code !== locale) ?? LOCALES[0];
  const currentLocale = LOCALES.find((entry) => entry.code === locale) ?? LOCALES[0];
  const themeChoice = useThemeStore((state) => state.choice);
  const setTheme = useThemeStore((state) => state.setChoice);
  const theme = themeChoice ?? "light";

  return (
    <Menu
      width="w-64"
      className="shrink-0"
      trigger={
        <span
          title={t("shell.accountMenu")}
          className="hover:bg-surface-inset flex items-center gap-2.5 rounded-full py-1 pr-2 pl-1 transition-colors"
        >
          <Avatar src={profile.avatar} name={name} className="size-8" />
          <span className="hidden max-w-32 min-w-0 text-left md:block lg:max-w-40">
            <span className="text-fg text-subhead block truncate font-medium">{name}</span>
            <span className="text-fg-muted text-footnote block truncate">
              {profile.user_name}
            </span>
          </span>
          <ChevronDown
            className="text-fg-subtle size-4 shrink-0 transition-transform group-open:rotate-180"
            aria-hidden
          />
        </span>
      }
    >
      <div className="flex items-center gap-3 px-2.5 py-2">
        <Avatar src={profile.avatar} name={name} className="size-9" />
        <span className="min-w-0">
          <span className="text-fg text-callout block truncate font-medium">{name}</span>
          <span className="text-fg-muted text-subhead block truncate">{profile.user_name}</span>
        </span>
      </div>
      <MenuSeparator />
      <Link to="/profile" className={menuItemClass()}>
        <UserRound aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t("shell.viewProfile")}</span>
      </Link>
      <Link to="/profile/password" className={menuItemClass()}>
        <KeyRound aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t("shell.changePassword")}</span>
      </Link>
      <MenuSeparator />
      <MenuItem
        icon={<Languages aria-hidden />}
        trailing={currentLocale.label}
        data-keep-open
        onClick={() => setLocale(nextLocale.code)}
      >
        {t("shell.language")}
      </MenuItem>
      <MenuItem
        icon={theme === "dark" ? <Moon aria-hidden /> : <Sun aria-hidden />}
        trailing={theme === "dark" ? t("theme.darkValue") : t("theme.lightValue")}
        data-keep-open
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        {t("shell.theme")}
      </MenuItem>
      <MenuSeparator />
      <MenuItem danger icon={<LogOut aria-hidden />} onClick={onSignOut}>
        {t("shell.logout")}
      </MenuItem>
    </Menu>
  );
}
